"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Image from "next/image";
import {
  LayoutDashboard,
  FileText,
  AlertTriangle,
  TrendingUp,
  PieChart,
  CheckCircle2,
  Users,
  Settings,
  ArrowRight,
} from "lucide-react";

// Ensure GSAP plugin is registered
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const CommitteeSection = () => {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse",
        },
      });

      // 1. Header Fade In
      tl.from(".mc-header", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        // 2. Dashboard Interface Pop Up
        .from(
          ".dashboard-ui",
          {
            y: 60,
            opacity: 0,
            scale: 0.95,
            duration: 0.8,
            ease: "back.out(1.2)",
          },
          "-=0.4",
        );

      // 3. Animate Charts inside Dashboard
      gsap.from(".chart-bar", {
        height: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".dashboard-ui",
          start: "top 70%",
        },
      });

      // 4. Animate Progress Bar (87%)
      gsap.fromTo(
        ".progress-fill",
        { width: "0%" },
        {
          width: "87%",
          duration: 1.5,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".dashboard-ui",
            start: "top 70%",
          },
        },
      );

      // 5. Stagger Features/Roles
      gsap.from(".feature-box", {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.1,
        scrollTrigger: {
          trigger: ".mc-features-grid",
          start: "top 80%",
        },
      });
    },
    { scope: containerRef },
  );

  return (
    <section
      ref={containerRef}
      className="py-24 bg-[#050505] px-4 relative overflow-hidden border-t border-white/5"
    >
      {/* Background Ambience */}
      <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-orange-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* HEADER */}
        <div className="mc-header text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-clash font-bold tracking-normal text-white mb-6 leading-tight">
            Everything Your <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
              Managing Committee
            </span>{" "}
            Needs.
          </h2>
          <p className="text-gray-400 text-lg font-medium max-w-2xl mx-auto">
            Collection dashboard. Compliance reports. Zero Excel.
            <br />
            Run your society like a Fortune 500 company.
          </p>
        </div>

        {/* --- MAIN DASHBOARD VISUAL (Hero Style - Force Desktop Layout on Mobile) --- */}
        <div className="dashboard-ui w-full h-auto md:h-[600px] bg-[#050505] rounded-3xl border border-white/10 flex text-left shadow-2xl overflow-hidden mb-20">
          {/* Sidebar - VISIBLE ON MOBILE (Removed 'hidden') */}
          <div className="flex w-10 md:w-20 border-r border-white/5 bg-[#080808] flex-col items-center py-4 md:py-6 gap-3 md:gap-6 z-20 shrink-0">
            <div className="w-5 h-5 md:w-8 md:h-8 bg-orange-500 rounded-md md:rounded-lg flex items-center justify-center shadow-[0_0_15px_rgba(249,115,22,0.5)]">
              <Image
                src="/brand/logo.svg"
                alt="SmartDwara Logo"
                width={32}
                height={32}
                className="w-4 h-4 md:w-8 md:h-8 rounded-sm md:rounded-xl"
              />
            </div>
            <div className="flex-1 flex flex-col gap-3 md:gap-6 mt-2 md:mt-4">
              <div className="p-1.5 md:p-2 text-gray-600 hover:text-white transition-colors">
                <Users className="w-3 h-3 md:w-5 md:h-5" />
              </div>
              <div className="p-1.5 md:p-2 bg-white/10 rounded-md md:rounded-lg text-white">
                <FileText className="w-3 h-3 md:w-5 md:h-5" />
              </div>
              <div className="p-1.5 md:p-2 text-gray-600 hover:text-white transition-colors">
                <TrendingUp className="w-3 h-3 md:w-5 md:h-5" />
              </div>
              <div className="p-1.5 md:p-2 text-gray-600 hover:text-white transition-colors">
                <Settings className="w-3 h-3 md:w-5 md:h-5" />
              </div>
            </div>
            <div className="w-5 h-5 md:w-8 md:h-8 rounded-full bg-gray-800 border border-white/10" />
          </div>

          {/* Main Content Area - Scaled Padding (p-2 md:p-8) */}
          <div className="flex-1 bg-[#0A0A0A] p-2 md:p-8 flex flex-col relative overflow-hidden">
            {/* Glossy Overlay */}
            <div className="absolute inset-0 bg-linear-to-br from-white/5 to-transparent pointer-events-none" />

            {/* Dashboard Header */}
            <div className="flex justify-between items-center mb-3 md:mb-8 relative z-10">
              <div>
                <h3 className="text-white font-bold text-xs md:text-lg">
                  Treasury Dashboard
                </h3>
                <p className="text-[8px] md:text-xs text-gray-500">
                  Overview: Jan 2026
                </p>
              </div>
              <div className="flex gap-3">
                <div className="px-2 md:px-3 py-1 md:py-1.5 bg-white/5 border border-white/10 rounded-full text-[8px] md:text-xs text-gray-400 flex items-center gap-1.5 md:gap-2">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-green-500 animate-pulse" />{" "}
                  <span className="hidden sm:inline">Compliance:</span> 100%
                </div>
              </div>
            </div>

            {/* Stats Row - FORCED 3 COLS (grid-cols-3) */}
            <div className="grid grid-cols-3 gap-2 md:gap-4 mb-3 md:mb-6 relative z-10">
              {/* Card 1: Collection */}
              <div className="bg-[#111] border border-white/5 p-2 md:p-4 rounded-lg md:rounded-xl">
                <div className="flex justify-between items-start mb-1 md:mb-2">
                  <p className="text-[7px] md:text-[10px] uppercase tracking-wider text-gray-500 font-bold truncate">
                    Collected
                  </p>
                  <TrendingUp className="w-3 h-3 md:w-4 md:h-4 text-green-500" />
                </div>
                <p className="text-sm md:text-2xl font-black text-white">87%</p>
                <div className="w-full h-1 md:h-1.5 bg-gray-800 rounded-full mt-1.5 md:mt-3 overflow-hidden">
                  <div className="progress-fill h-full bg-linear-to-r from-orange-500 to-amber-500 rounded-full w-[87%]" />
                </div>
                <p className="text-[7px] md:text-[10px] text-gray-500 mt-1 md:mt-2 truncate">
                  43/50 Paid
                </p>
              </div>

              {/* Card 2: Overdue */}
              <div className="bg-[#111] border border-white/5 p-2 md:p-4 rounded-lg md:rounded-xl">
                <div className="flex justify-between items-start mb-1 md:mb-2">
                  <p className="text-[7px] md:text-[10px] uppercase tracking-wider text-gray-500 font-bold truncate">
                    Overdue
                  </p>
                  <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 text-red-500" />
                </div>
                <p className="text-sm md:text-2xl font-black text-white">
                  ₹28.5k
                </p>
                <div className="flex gap-2 mt-1.5 md:mt-3">
                  <span className="px-1.5 py-0.5 md:px-2 bg-red-500/10 border border-red-500/20 rounded text-[7px] md:text-[9px] text-red-400 font-bold whitespace-nowrap">
                    4 Pending
                  </span>
                </div>
              </div>

              {/* Card 3: Defaulters */}
              <div className="bg-[#111] border border-white/5 p-2 md:p-4 rounded-lg md:rounded-xl relative overflow-hidden">
                <div className="absolute right-0 top-0 w-8 h-8 md:w-16 md:h-16 bg-red-500/10 blur-xl rounded-full" />
                <p className="text-[7px] md:text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 truncate">
                  Defaulters
                </p>
                <div className="space-y-1 md:space-y-2 mt-1 md:mt-2">
                  <div className="flex justify-between text-[8px] md:text-xs items-center">
                    <span className="text-white font-bold">101</span>
                    <span className="text-red-400 font-mono">₹12k</span>
                  </div>
                  <div className="w-full h-px bg-white/5" />
                  <div className="flex justify-between text-[8px] md:text-xs items-center">
                    <span className="text-white font-bold">205</span>
                    <span className="text-red-400 font-mono">₹8.5k</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Row - FORCED 2 COLS (grid-cols-2) */}
            <div className="flex-1 grid grid-cols-2 gap-2 md:gap-4 relative z-10 min-h-0">
              {/* Bar Chart Panel */}
              <div className="bg-[#111] border border-white/5 p-2 md:p-5 rounded-lg md:rounded-xl flex flex-col h-full">
                <div className="flex justify-between items-center mb-2 md:mb-4">
                  <h4 className="text-[8px] md:text-xs font-bold text-white uppercase tracking-wider truncate">
                    Recovery
                  </h4>
                  <span className="text-[7px] md:text-[10px] text-gray-500">
                    6 Mo
                  </span>
                </div>
                <div className="flex-1 flex items-end justify-between gap-1 md:gap-2 px-1">
                  {[40, 65, 45, 80, 55, 90].map((h, i) => (
                    <div
                      key={i}
                      className="w-full bg-gray-800/50 rounded-t-[1px] md:rounded-t-sm relative group h-full flex flex-col justify-end"
                    >
                      <div
                        className="chart-bar w-full bg-orange-500/80 hover:bg-orange-500 transition-colors rounded-t-[1px] md:rounded-t-sm"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-1 md:mt-2 text-[6px] md:text-[9px] text-gray-600 font-mono uppercase">
                  <span>A</span>
                  <span>S</span>
                  <span>O</span>
                  <span>N</span>
                  <span>D</span>
                  <span>J</span>
                </div>
              </div>

              {/* Pie Chart / Compliance Panel */}
              <div className="bg-[#111] border border-white/5 p-2 md:p-5 rounded-lg md:rounded-xl flex flex-col h-full">
                <div className="flex justify-between items-center mb-1 md:mb-2">
                  <h4 className="text-[8px] md:text-xs font-bold text-white uppercase tracking-wider truncate">
                    Audit
                  </h4>
                  <span className="text-[7px] md:text-[10px] text-green-500 font-bold">
                    OK
                  </span>
                </div>

                <div className="flex-1 flex items-center justify-between">
                  {/* Pie Chart */}
                  <div className="w-10 h-10 md:w-24 md:h-24 rounded-full bg-[conic-gradient(#f97316_0%_65%,#333_65%_100%)] relative flex items-center justify-center shrink-0">
                    <div className="w-7 h-7 md:w-16 md:h-16 bg-[#111] rounded-full flex flex-col items-center justify-center">
                      <span className="text-[8px] md:text-xs font-black text-white">
                        65%
                      </span>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex-1 pl-2 md:pl-6 space-y-1.5 md:space-y-3">
                    <div>
                      <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-orange-500 rounded-full" />
                        <span className="text-[8px] md:text-xs text-white font-bold">
                          Sink
                        </span>
                      </div>
                      <p className="text-[7px] md:text-[10px] text-gray-500 ml-2.5 md:ml-4">
                        ₹12L
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1">
                        <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#333] rounded-full" />
                        <span className="text-[8px] md:text-xs text-white font-bold">
                          Rep
                        </span>
                      </div>
                      <p className="text-[7px] md:text-[10px] text-gray-500 ml-2.5 md:ml-4">
                        ₹8L
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* --- FEATURES GRID (Compliance & Pain Points) --- */}
        <div className="mc-features-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {/* Compliance Card */}
          <div className="feature-box lg:col-span-2 bg-[#111] border border-white/10 p-8 rounded-3xl">
            <h3 className="text-xl font-clash font-semibold text-white mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              100% Compliance Ready
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                "MCS Act 1960 Compliant",
                "Sinking Fund Tracked",
                "Repair Fund Audited",
                "AGM Reports Auto-Gen",
                "ROC Filing Ready",
                "GST Logic Built-in",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                  <span className="text-sm text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pain Points Card */}
          <div className="feature-box lg:col-span-2 bg-[#111] border border-white/10 p-8 rounded-3xl">
            <h3 className="text-xl font-clash font-semibold text-white mb-6 flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5 text-orange-500" />
              Goodbye, Excel Nightmares
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-sm text-gray-400">
                  Arrears Calculation
                </span>
                <span className="text-sm font-bold text-white">
                  Auto (18% Simple)
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-sm text-gray-400">Non-Occupancy</span>
                <span className="text-sm font-bold text-white">
                  Capped @ 10%
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-sm text-gray-400">Parking Tracking</span>
                <span className="text-sm font-bold text-white">
                  Slot-wise Mapping
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --- ROLES & SETUP --- */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Role Based Access */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-clash font-semibold text-white mb-6">
              Granular Role Access
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  role: "Chairman",
                  access: "Full Access + Reports",
                  icon: Users,
                },
                {
                  role: "Secretary",
                  access: "Bill Generation + Notices",
                  icon: FileText,
                },
                {
                  role: "Treasurer",
                  access: "Payments + Collections",
                  icon: PieChart,
                },
                {
                  role: "Committee",
                  access: "View-Only Dashboard",
                  icon: Settings,
                },
              ].map((role, i) => (
                <div
                  key={i}
                  className="feature-box bg-[#0A0A0A] border border-white/5 p-4 rounded-xl flex items-center gap-4 hover:border-orange-500/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-orange-500">
                    <role.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">
                      {role.role}
                    </h4>
                    <p className="text-xs text-gray-500">{role.access}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Setup Box */}
          <div className="feature-box bg-linear-to-br from-[#111] to-black border border-white/10 p-8 rounded-3xl h-full flex flex-col justify-center">
            <h3 className="text-xl font-clash font-semibold text-white mb-6">
              Live in 3 Minutes
            </h3>
            <div className="space-y-6 relative">
              {/* Vertical Line */}
              <div className="absolute left-3.5 top-2 bottom-2 w-0.5 bg-white/10" />

              {[
                "Enter Society Rates",
                "Upload Flat List",
                "Generate First Bill",
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-4 relative z-10">
                  <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-xs shadow-lg shadow-orange-900/50">
                    {i + 1}
                  </div>
                  <span className="text-gray-300 text-sm font-medium">
                    {step}
                  </span>
                </div>
              ))}
            </div>

            <button className="w-full mt-8 py-3 bg-orange-500 text-white hover:text-orange-500 hover:bg-gray-200  font-clash font-semibold uppercase tracking-wid rounded-xl flex items-center justify-center gap-2 transition-colors">
              Book Live Demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommitteeSection;
